// Diagnostic instrumentation for the OAuth code-exchange failure class —
// primarily the iOS `pkce_code_verifier_not_found` bug (issue: iOS PKCE OAuth).
//
// We CANNOT reproduce that failure (a clean device never triggers it; ~4
// affected iOS users/day in prod), and session replay can't see it (the cookie
// loss happens off-app during the Google bounce). So instead of guessing
// (#523's blind Secure-flag fix failed), we capture the REAL failure conditions
// from affected users at the moment `exchangeCodeForSession` rejects, then query
// PostHog to learn the mechanism: was the code-verifier cookie the server reads
// ever present (never-set vs not-sent), what arrived alongside it, and from
// what context (sec-fetch-site / referer host / UA → in-app browser,
// cross-site bounce).
//
// SAFETY: logs cookie NAMES only, never values (the session cookie value is a
// JWT). The referer is reduced to its host (drops the `?code=...` query).

/** Build the diagnostic property bag for an OAuth code-exchange failure.
 *  Pure (takes cookie names + headers) so it's unit-testable without a request. */
export function oauthFailureDiagnostics(args: {
  cookieNames: readonly string[];
  headers: Headers;
  errorCode: string | undefined;
  /** Was this failure the one-shot auto-retry round-trip (#685 fix)?
   *  Rescue rate = 1 − (retried=true count / retried=false count), computed
   *  WITHIN the retriable class: filter error_code to
   *  CONTEXT_ISOLATION_ERROR_CODES members (`lib/auth/errors.ts`), or the
   *  denominator inflates with non-retriable first failures
   *  (flow_state_expired etc.) that never triggered a retry. */
  retried: boolean;
}): Record<string, unknown> {
  const { cookieNames, headers, errorCode, retried } = args;
  // PRINCIPLE: ship only the irreducible inputs PostHog can't reconstruct.
  // Every count/flag is derived from `sb_cookie_names` at query time — shipping
  // a pre-computed scalar alongside the array is redundant noise (three
  // /simplify passes removed exactly that: verifier_cookie_present, then
  // sb_cookie_count, then verifier_cookie_count). The verifier and session
  // cookies are all `sb-*`, so the names array answers every question the
  // probe asks.
  //
  // Cookie names (`<ref>` is `api` in prod): since supabase-js 2.118 an OAuth
  // start writes a per-flow slot `sb-<ref>-auth-token-flow-<id>-code-verifier`,
  // its index `sb-<ref>-auth-token-flows-code-verifier`, and the fixed
  // `sb-<ref>-auth-token-code-verifier` (auth-js's deprecation-window mirror of
  // the newest flow). No flow id rides our redirect, so the callback reads only
  // the fixed key, and a successful exchange deletes only that one: slots from
  // earlier sign-ins stay in the jar. So never match '%code-verifier%', which
  // still matches when the key the server reads is gone. The session is
  // `sb-<ref>-auth-token`, chunked `.0`, `.1`, ... when large.
  // pkce-verifier-cookies.test.ts pins these names against the real libraries.
  //
  // PostHog recipes. The property is a JSON string, so first take
  // names = JSONExtract(properties.sb_cookie_names, 'Array(String)'):
  //   the verifier the server reads is present (never-set vs not-sent):
  //     arrayExists(x -> endsWith(x, '-auth-token-code-verifier'), names)
  //   session already present (existing user vs fresh), chunked or not:
  //     arrayExists(x -> match(x, '-auth-token([.][0-9]+)?$'), names)
  return {
    error_code: errorCode ?? "unknown",
    retried,
    sb_cookie_names: cookieNames.filter((n) => n.startsWith("sb-")).sort(),
    // Context: cross-site bounce / in-app browser markers.
    sec_fetch_site: headers.get("sec-fetch-site"),
    sec_fetch_mode: headers.get("sec-fetch-mode"),
    sec_fetch_dest: headers.get("sec-fetch-dest"),
    referer_host: refererHost(headers.get("referer")),
    user_agent: headers.get("user-agent"),
  };
}

/** Host portion of a referer URL (drops path + the sensitive `?code=` query).
 *  Returns null for absent/unparseable referers. Shared with the client-side
 *  probe (`oauth-failure-client-context.ts`), which reduces `document.referrer`
 *  the same way. */
export function refererHost(referer: string | null): string | null {
  if (!referer) return null;
  try {
    return new URL(referer).host;
  } catch {
    return null;
  }
}

/** Extract the PostHog `distinct_id` from the `ph_<key>_posthog` cookie so the
 *  failure event ties to the user's existing PostHog person (their device +
 *  other events) instead of a fresh anonymous id. Returns null if the cookie is
 *  absent or unparseable. */
export function posthogDistinctIdFromCookies(
  cookies: readonly { name: string; value: string }[],
): string | null {
  const phCookie = cookies.find(
    (c) => c.name.startsWith("ph_") && c.name.endsWith("_posthog"),
  );
  if (!phCookie) return null;
  try {
    const parsed = JSON.parse(decodeURIComponent(phCookie.value)) as {
      distinct_id?: unknown;
    };
    return typeof parsed.distinct_id === "string" ? parsed.distinct_id : null;
  } catch {
    return null;
  }
}
